import EventEmitter from '@/common/EventEmitter';
import { CoSyncCenterEvent } from '@/renderer/scripts/CoSync/SyncCenter/CoSyncCenterEvent';
import { CoSyncTaskDB, getCoSyncTaskDB } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncTaskDB';
import SyncGlobalRepo from '@/renderer/scripts/CoSync/SyncCenter/Repo/SyncGlobalRepo';
import SyncHistoryDAO from '@/renderer/scripts/CoSync/SyncCenter/Repo/SyncHistoryDAO';
import ISyncServiceCommunicatorProxySide from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommunicator/ISyncServiceCommunicatorProxySide';
import SyncServiceIpcCommunicatorRenderSide from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommunicator/SyncServiceIpcCommunicatorRenderSide';
import { CmdFromSyncTask, SyncTaskUIStatus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenter';
import { CoSyncReporter } from '@/renderer/views/desktop/FileManagement/FileSync/composition/CoSyncReporter';
import {
  CoSyncInfo,
  CoSyncTimingType,
  getName,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import {
  CoSyncStatus,
  CoSynStatusInfo,
  SyncBaseStatusInfo,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import WorkerProcessIPCHepler from '@/renderer/scripts/utils/WorkerProcess/WorkerProcessIPCHepler';
import CoSyncDeleteCache from '@/workerProcessRenderer/scripts/CoSyncEngine/Operator/CoSyncDeleteCache';

import { DeleteSyncParams, QuerySyncInfoData } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { TimingStrategyDelegate } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/TimingStrategy/TimingStrategyDelegate';
import { ITimingStrategy } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/TimingStrategy/ITimingStrategy';
import { TimingStrategyFactory } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/TimingStrategy/TimingStrategyFactory';
import SyncTreeDAO from '@/workerProcessRenderer/scripts/CoSyncEngine/Tree/SyncTreeDAO';
import smh from '@/renderer/scripts/smh';
import { CmdFromWorker, CmdTypeFromWorker } from '../ServiceCommand/CmdFromWorker';
import {
  CmdFromRenderer,
  CmdTypeFromRender,
  DestoryCmdParam,
  FetchOperateTaskListResult,
  QueryHistoryCmdParam,
  StartCmdparam,
  StopCmdParam,
  UpdateCoSyncInfoParam,
  UpdateCoSyncSettingParam,
} from '../ServiceCommand/CmdFromRenderer';

/**
 *
 *
 * @export
 * @class SyncServiceProxy
 * @extends {EventEmitter}
 * 主renderer进程的同步服务Proxy(基类)
 */
export default class SyncServiceProxy extends EventEmitter implements TimingStrategyDelegate {
  private communicator: ISyncServiceCommunicatorProxySide | null;
  private coSyncInfo: CoSyncInfo;
  private readonly userInfo: CoFile.UserInfo;

  private readonly taskDB: CoSyncTaskDB;
  private readonly historyDAO: SyncHistoryDAO;
  private readonly syncTreeDAO: SyncTreeDAO;
  private readonly syncGlobalRepo: SyncGlobalRepo;

  private isStarted: boolean;

  private reTurnOnEngineCmd: CmdFromRenderer | null;
  private reTurnOnEngineCnt: number;

  private replayStartListenOperateTaskCmd: CmdFromRenderer | null;

  private currentStatusInfo: CoSynStatusInfo;

  private timingStrategy: ITimingStrategy;

  constructor(userInfo: CoFile.UserInfo, config: CoSyncInfo, syncGlobalRepo: SyncGlobalRepo) {
    super();

    logger.ui.info('SyncServiceProxy constructor', userInfo, config);
    logger.ui.info('SyncServiceProxy constructor name', getName(config.config.syncPath));

    this.communicator = null;
    this.coSyncInfo = config;
    this.userInfo = userInfo;

    this.taskDB = getCoSyncTaskDB(config.localSyncId);
    this.historyDAO = new SyncHistoryDAO(this.taskDB);
    this.syncTreeDAO = new SyncTreeDAO(config.localSyncId);
    this.syncGlobalRepo = syncGlobalRepo;

    this.timingStrategy = TimingStrategyFactory.createTimingStrategyFrom(new WeakRef(this), this.coSyncInfo);

    this.isStarted = false;

    this.reTurnOnEngineCmd = null;
    this.reTurnOnEngineCnt = 0;

    this.replayStartListenOperateTaskCmd = null;

    this.currentStatusInfo = this.buildStopStatus();
  }

  /**
   * @description 主渲染进程与worker进程通信(双向)
   * @param {CmdFromRenderer} cmd
   * @type QUERY_HISTORY 查询历史记录
   * @return {*}  {Promise<any>}
   * @memberof SyncServiceProxy
   */
  public async applyCommand(cmd: CmdFromRenderer): Promise<any> {
    logger.ui.info(`SyncServiceProxy applyCommand `, cmd, this.coSyncInfo);
    switch (cmd.cmdType) {
      case CmdTypeFromRender.START: {
        return this.onStart(cmd);
      }

      case CmdTypeFromRender.TURN_ON_ENGINE: {
        return this.onTurnOnEngine(cmd);
      }

      case CmdTypeFromRender.STOP: {
        return this.onStop(cmd);
      }

      case CmdTypeFromRender.TURN_OFF_ENGINE: {
        return this.onTurnOffEngine(cmd);
      }

      case CmdTypeFromRender.DESTORY: {
        // worker不在时
        if (!this.communicator) {
          return Promise.resolve();
        }

        // 在删除场景，超时当作成功，避免UI出现一直超时删不掉的情况
        try {
          await this.communicator.send(cmd);
          return;
        } catch (error: any) {
          if (error?.message === WorkerProcessIPCHepler.WORKER_PROCESS_TIMEOUT_ERROR) {
            return;
          }

          logger.ui.error('communicator send error', cmd, error, this.coSyncInfo);
          throw error;
        }
      }

      case CmdTypeFromRender.CONFLICT_RESOLVE: {
        // worker不在时
        if (!this.communicator) {
          return Promise.reject({ code: 'WorkerNotExist' });
        }

        return this.communicator.send(cmd);
      }

      case CmdTypeFromRender.FETCH_OPERATE_TASK_LIST: {
        // worker不在时
        if (!this.communicator) {
          return Promise.resolve({
            allCount: 0,
            finishCount: 0,
            exceptionCount: 0,
            taskInfoList: [],
          } as FetchOperateTaskListResult);
        }

        const res: FetchOperateTaskListResult = await this.communicator.send(cmd);
        logger.ui.debug('SyncServiceProxy FETCH_OPERATE_TASK_LIST return', JSON.stringify(res));
        return res;
      }

      case CmdTypeFromRender.START_LISTEN_OPERATE_TASK: {
        // worker不在时
        if (!this.communicator) {
          this.replayStartListenOperateTaskCmd = cmd;
          return Promise.resolve();
        }

        return this.communicator.send(cmd);
      }

      case CmdTypeFromRender.STOP_LISTEN_OPERATE_TASK: {
        // worker不在时
        if (!this.communicator) {
          this.replayStartListenOperateTaskCmd = null;
          return Promise.resolve();
        }

        return this.communicator.send(cmd);
      }

      case CmdTypeFromRender.QUERY_HISTORY: {
        const param = cmd.param as QueryHistoryCmdParam;
        return this.historyDAO.queryHistory(param);
      }

      case CmdTypeFromRender.CLEAR_HISTORY: {
        return this.historyDAO.removeAll();
      }

      case CmdTypeFromRender.UPDATE_CO_SYNC_INFO: {
        // worker不在时
        if (!this.communicator) {
          return Promise.resolve();
        }

        return this.communicator.send(cmd);
      }

      case CmdTypeFromRender.UPDATE_CO_SYNC_SETTING: {
        const updateCoSyncSettingParam = cmd.param as UpdateCoSyncSettingParam;
        return this.onUpdateCoSyncSetting(updateCoSyncSettingParam);
      }

      default: {
        const _exhaustiveCheck: never = cmd.cmdType;
        return _exhaustiveCheck;
      }
    }
  }

  /**
   * @description 销毁
   * @param {boolean} isPermanent 是否要删数据库
   * @memberof SyncServiceProxy
   */
  public async destory(isPermanent: boolean) {
    logger.ui.info(`SyncServiceProxy destory isPermanent:${isPermanent}, localSyncId: ${this.coSyncInfo.localSyncId}`);
    const destoryParam: DestoryCmdParam = {
      isPermanent,
    };
    const destoryCommand: CmdFromRenderer = {
      cmdType: CmdTypeFromRender.DESTORY,
      param: destoryParam,
    };
    await this.applyCommand(destoryCommand);
    await this.deprecateCommunicator();
    if (isPermanent) {
      smh.userAPI.deleteSync({
        syncId: this.coSyncInfo.syncId,
      } as DeleteSyncParams);

      // 删除DB配置前确保删除缓存文件
      CoSyncDeleteCache.deleteSyncCache(this.coSyncInfo.config.syncPath.localPath, this.coSyncInfo.localSyncId);
      this.taskDB.deleteDB(); // 不等DB结束，避免不是主流程的逻辑卡住ui
      this.syncTreeDAO.removeDb();
    }
  }

  /**
   * @description 启动
   * @private
   * @param {CmdFromRenderer} cmd
   * @return {*}  {Promise<void>}
   * @memberof SyncServiceProxy
   */
  private async onStart(cmd: CmdFromRenderer): Promise<void> {
    this.isStarted = true;
    const startParam = cmd.param as StartCmdparam;
    // ui触发的开始要记录下来，下一次启动自动开始
    if (startParam.isFromUIAction) {
      this.coSyncInfo.config.syncSetting.keepWhichLastStatus = 'started';
      this.syncGlobalRepo.updateConfig(this.coSyncInfo);
    }

    await this.timingStrategy.handleStart();
  }

  /**
   * @description 启动引擎
   * @private
   * @param {CmdFromRenderer} cmd
   * @return {*}  {Promise<void>}
   * @memberof SyncServiceProxy
   */
  private async onTurnOnEngine(cmd: CmdFromRenderer): Promise<void> {
    this.reTurnOnEngineCmd = cmd; // 在启动时存下参数，这样在crash时就可以重新启动

    await this.deprecateCommunicator();

    this.emitTaskChange(this.buildWaitForRunningStatus()); // 创建进程前，由当前进程通知一次等待运行

    await this.setupCommunicator().send(cmd);

    if (this.replayStartListenOperateTaskCmd) {
      await this.applyCommand(this.replayStartListenOperateTaskCmd);
    }
  }

  /**
   * @description 停止
   * @private
   * @param {CmdFromRenderer} cmd
   * @return {*}  {Promise<void>}
   * @memberof SyncServiceProxy
   */
  private async onStop(cmd: CmdFromRenderer): Promise<void> {
    this.isStarted = false;

    const stopParam = cmd.param as StopCmdParam;
    // ui触发的停止要记录下来，下一次启动不要自动开始
    if (stopParam.isFromUIAction) {
      this.coSyncInfo.config.syncSetting.keepWhichLastStatus = 'stopped';
      this.syncGlobalRepo.updateConfig(this.coSyncInfo);
    }

    await this.timingStrategy.handleStop(stopParam);
  }

  /**
   * @description 结束引擎
   * @private
   * @param {CmdFromRenderer} cmd
   * @return {*}  {Promise<void>}
   * @memberof SyncServiceProxy
   */
  private async onTurnOffEngine(cmd: CmdFromRenderer): Promise<void> {
    this.reTurnOnEngineCmd = null; // 在停止时清除参数，这样在Crash时就不会重新启动

    if (!this.communicator) {
      return Promise.resolve();
    }

    // 在停止场景，超时当作成功，避免UI出现一直超时关不掉进程的情况
    try {
      await this.communicator.send(cmd);
      return;
    } catch (error: any) {
      if (error?.message === WorkerProcessIPCHepler.WORKER_PROCESS_TIMEOUT_ERROR) {
        // 超时补充通知停止，让UI是正常的
        this.emitTaskChange(this.buildStopStatus());
        return;
      }

      logger.ui.warn(`${JSON.stringify(error)}, ${JSON.stringify(this.coSyncInfo)}`);
      throw error;
    } finally {
      await this.deprecateCommunicator();
    }
  }

  /**
   * @description 建立连接
   * @private
   * @return {*}  {ISyncServiceCommunicatorProxySide}
   * @memberof SyncServiceProxy
   */
  private setupCommunicator(): ISyncServiceCommunicatorProxySide {
    this.communicator = new SyncServiceIpcCommunicatorRenderSide(this.userInfo, this.coSyncInfo);
    this.communicator.attachNotifyHandler(this.onWorkerSideNotify);
    this.communicator.attachWorkerCrashHandler?.(this.onWorkerCrashNotify);
    return this.communicator;
  }

  private async deprecateCommunicator(): Promise<void> {
    if (this.communicator) {
      await this.communicator.deinit();
      this.communicator = null;
    }
  }

  // 收到worker进程通知
  private readonly onWorkerSideNotify = (cmd: CmdFromWorker): Promise<any> => {
    logger.ui.info(`SyncServiceProxy onWorkerSideNotify cmd:${JSON.stringify(cmd)}, ${this.coSyncInfo.localSyncId}`);
    if (cmd.cmdType === CmdTypeFromWorker.UPDATE_STATUS) {
      const status = cmd.param as CoSynStatusInfo;
      return this.onUpdateStatus(status);
    }

    // 其他命令上抛UI层做透传
    const payload: CmdFromSyncTask = {
      coSyncInfo: this.coSyncInfo,
      cmd,
    };
    this.emit(CoSyncCenterEvent.onWorkerCmd, payload);
    return Promise.resolve('');
  };

  // 收到worker进程crash通知
  private readonly onWorkerCrashNotify = async (): Promise<void> => {
    logger.ui.info(`SyncServiceProxy onWorkerCrashNotify ${this.coSyncInfo.localSyncId}`);
    if (this.reTurnOnEngineCnt >= 3) return;
    if (!this.reTurnOnEngineCmd) return;

    logger.ui.warn('worker restart after crash');
    await this.deprecateCommunicator();
    ++this.reTurnOnEngineCnt;
    this.applyCommand(this.reTurnOnEngineCmd);
  };

  // 通知更新状态
  private async onUpdateStatus(status: CoSynStatusInfo): Promise<void> {
    CoSyncReporter.getInstance().onTaskStatusChange(status, this.coSyncInfo);

    if (status.status === CoSyncStatus.STATUS_ERROR) {
      await this.deprecateCommunicator();
    }

    this.timingStrategy.onUpdateStatus(status);
  }

  private buildWaitForRunningStatus(): CoSynStatusInfo {
    const waitRunningStatusInfo: SyncBaseStatusInfo = {
      syncId: this.coSyncInfo.syncId,
      status: CoSyncStatus.STATUS_WAIT_FOR_RUNNING,
    };
    return waitRunningStatusInfo;
  }

  public buildStopStatus(): CoSynStatusInfo {
    const stopStatusInfo: SyncBaseStatusInfo = {
      syncId: this.coSyncInfo.syncId,
      status: CoSyncStatus.STATUS_STOP,
    };
    return stopStatusInfo;
  }

  public buildIdleStatus(): CoSynStatusInfo {
    const idleStatusInfo: SyncBaseStatusInfo = {
      syncId: this.coSyncInfo.syncId,
      status: CoSyncStatus.STATUS_IDLE,
    };

    return idleStatusInfo;
  }

  // 处理初始状态
  public handleJustCreated() {
    // TODO 临时屏蔽缓存
    // const cachePath = getSyncDeleteCachePath(this.coSyncInfo.config.syncPath.localPath, this.coSyncInfo.localSyncId);
    // createLocalDir(cachePath);
    this.timingStrategy.handleJustCreated();
  }

  // 根据上一次关闭前用户的操作来判断本次启动App是否自动运行
  public handleRestore() {
    this.timingStrategy.handleRestore();
  }

  public getCoSyncInfo(): CoSyncInfo {
    return this.coSyncInfo;
  }

  public updateCoSyncInfo(data: QuerySyncInfoData) {
    let changed = false;

    // 远程路径发生变化
    const couldPath = data.cloudPath.join('/');
    if (couldPath !== this.coSyncInfo.config.syncPath.remotePath) {
      changed = true;

      // 目前只处理远端路径变化，权限问题/解除问题不在这里处理，由底层执行时报错统一处理

      // 更新文件夹名称
      this.coSyncInfo.config.syncPath.remotePath = couldPath;
    }

    if (changed) {
      const coSyncInfo: any = {};
      Object.assign(coSyncInfo, this.coSyncInfo);
      this.coSyncInfo = coSyncInfo;

      // 通知worker变化
      const updateCoSyncInfoParam: UpdateCoSyncInfoParam = {
        coSyncInfo: this.coSyncInfo,
      };
      const updateCoSyncInfoCmd: CmdFromRenderer = {
        cmdType: CmdTypeFromRender.UPDATE_CO_SYNC_INFO,
        param: updateCoSyncInfoParam,
      };
      this.applyCommand(updateCoSyncInfoCmd);

      // 更新DB
      this.syncGlobalRepo.updateConfig(this.coSyncInfo);

      // 通知UI变化
      this.emitTaskChange(this.currentStatusInfo);
    }
  }

  public emitTaskChange(statusInfo: CoSynStatusInfo) {
    this.currentStatusInfo = statusInfo;

    const taskStatus: SyncTaskUIStatus = {
      coSyncInfo: this.coSyncInfo,
      statusInfo,
    };

    this.emit(CoSyncCenterEvent.onTaskStatusChange, taskStatus);
  }

  private async onUpdateCoSyncSetting(updateCoSyncSettingParam: UpdateCoSyncSettingParam): Promise<void> {
    const isTimingTypeChanged =
      this.coSyncInfo.config.syncSetting.timingType !== updateCoSyncSettingParam.coSyncSetting.timingType;
    this.coSyncInfo.config.syncSetting = updateCoSyncSettingParam.coSyncSetting;
    this.syncGlobalRepo.updateConfig(this.coSyncInfo);
    if (!isTimingTypeChanged) {
      return;
    }

    // 根据不同的当前状态进行不同的处理
    // 如果当前是未运行，那么就只更新配置
    if (!this.isStarted) {
      this.timingStrategy = TimingStrategyFactory.createTimingStrategyFrom(new WeakRef(this), this.coSyncInfo);
      if (this.coSyncInfo.config.syncSetting.timingType === CoSyncTimingType.AUTO) {
        await this.timingStrategy.handleStart();
      }
      return;
    }

    await this.timingStrategy.handleStop({ isFromUIAction: false } as StopCmdParam);
    this.timingStrategy = TimingStrategyFactory.createTimingStrategyFrom(new WeakRef(this), this.coSyncInfo);
    if (this.coSyncInfo.config.syncSetting.timingType === CoSyncTimingType.AUTO) {
      await this.timingStrategy.handleStart();
    }
  }
}
